import React, { createContext, useContext, useCallback, useRef, useEffect } from 'react';

interface NavigationBlocker {
  id: string;
  shouldBlock: () => boolean;
  onBlock: (continueNavigation: () => void) => void;
}

interface NavigationBlockContextType {
  registerBlocker: (blocker: NavigationBlocker) => void;
  unregisterBlocker: (id: string) => void;
  checkAndNavigate: (navigateFn: () => void) => void;
}

const NavigationBlockContext = createContext<NavigationBlockContextType | null>(null);

export const NavigationBlockProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const blockersRef = useRef<Map<string, NavigationBlocker>>(new Map());
  const isHandlingPopState = useRef(false);

  const registerBlocker = useCallback((blocker: NavigationBlocker) => {
    blockersRef.current.set(blocker.id, blocker);
  }, []);

  const unregisterBlocker = useCallback((id: string) => {
    blockersRef.current.delete(id);
  }, []);

  const findActiveBlocker = useCallback((): NavigationBlocker | null => {
    for (const blocker of blockersRef.current.values()) {
      if (blocker.shouldBlock()) {
        return blocker;
      }
    }
    return null;
  }, []);

  const checkAndNavigate = useCallback((navigateFn: () => void) => {
    const activeBlocker = findActiveBlocker();
    
    if (activeBlocker) {
      activeBlocker.onBlock(navigateFn);
    } else {
      navigateFn();
    }
  }, [findActiveBlocker]);

  useEffect(() => {
    const handlePopState = () => {
      if (isHandlingPopState.current) return;

      const activeBlocker = findActiveBlocker();
      
      if (activeBlocker) {
        isHandlingPopState.current = true;
        const currentPath = window.location.pathname + window.location.search;
        
        window.history.pushState(null, '', currentPath);
        
        activeBlocker.onBlock(() => {
          isHandlingPopState.current = false;
          window.history.back();
        });
        
        setTimeout(() => {
          isHandlingPopState.current = false;
        }, 100);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [findActiveBlocker]);

  return (
    <NavigationBlockContext.Provider value={{ registerBlocker, unregisterBlocker, checkAndNavigate }}>
      {children}
    </NavigationBlockContext.Provider>
  );
};

export const useNavigationBlock = (): NavigationBlockContextType => {
  const context = useContext(NavigationBlockContext);
  if (!context) {
    throw new Error('useNavigationBlock must be used within a NavigationBlockProvider');
  }
  return context;
};

export const useRegisterNavigationBlocker = (
  id: string,
  shouldBlock: () => boolean,
  onBlock: (continueNavigation: () => void) => void,
  deps: React.DependencyList = []
) => {
  const { registerBlocker, unregisterBlocker } = useNavigationBlock();

  useEffect(() => {
    registerBlocker({ id, shouldBlock, onBlock });
    return () => unregisterBlocker(id);
  }, [id, registerBlocker, unregisterBlocker, ...deps]);
};

export default NavigationBlockContext;
